"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";

const fadeInLeft = {
  initial: { opacity: 0, x: -60 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8 }
};

const fadeInRight = {
  initial: { opacity: 0, x: 60 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, delay: 0.2 }
};

// CDN Configuration - Choose your preferred CDN
const CDN_CONFIG = {
  // Option 1: Builder.io (Current - Very Fast)
  builderIO: {
    baseUrl: 'https://api.builder.io/api/v1/image/assets/TEMP/',
    params: '?width=2720&format=webp&quality=80'
  },

  // Option 2: Cloudinary (Recommended Alternative)
  cloudinary: {
    baseUrl: 'https://res.cloudinary.com/your-cloud-name/image/upload/',
    params: 'w_2720,f_auto,q_auto:good/'
  },

  // Option 3: ImageKit
  imageKit: {
    baseUrl: 'https://ik.imagekit.io/your-imagekit-id/',
    params: 'tr=w-2720,f-auto,q-80/'
  }
};

function HeroSection() {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  // Image URLs with different CDN options
  const imageUrls = {
    // Current Builder.io URL (very fast)
    primary: 'https://cdn.acsdev.in/Rentastic/new.webp',

    // Fallback URLs for different screen sizes
    mobile: 'https://cdn.acsdev.in/Rentastic/new.webp',
    tablet: 'https://cdn.acsdev.in/Rentastic/new.webp',

    // Blur placeholder for instant loading
    // placeholder: 'https://cdn.acsdev.in/Rentastic/10.webp'
        placeholder: 'https://cdn.acsdev.in/Rentastic/new.webp'
  };

  // Optimized image loading with preload
  useEffect(() => {
    // Preload the main image
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrls.primary;
    document.head.appendChild(link);

    // Load image with progressive enhancement
    const img = new Image();
    img.onload = () => {
      setImageSrc(imageUrls.primary);
      setImageLoaded(true);
    };
    img.onerror = () => {
      // Fallback to a different CDN or local image
      console.warn('Primary image failed to load, using fallback');
      setImageSrc(imageUrls.primary); // Keep same for now
      setImageLoaded(true);
    };
    img.src = imageUrls.primary;

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleShopNow = () => {
    navigate('/products');
  };

  // Generate responsive background image CSS
  const getBackgroundStyle = () => {
    if (!imageLoaded) {
      return {
        backgroundImage: `url(${imageUrls.placeholder})`,
        backgroundColor: '#e99094',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        filter: 'blur(5px)',
        transform: 'scale(1.1)' // Prevent blur edges
      };
    }

    return {
      backgroundImage: `url(${imageSrc})`,
      backgroundColor: 'transparent',
      backgroundSize: "cover",
      backgroundPosition: "top center",
      backgroundRepeat: "no-repeat"
    };
  };

  return (
    <section
      className="flex overflow-hidden relative justify-between items-center px-16 py-32 m-10 rounded-3xl max-md:flex-col max-md:px-5 max-md:py-16 max-md:text-center max-sm:px-4 max-sm:py-10 max-sm:m-4 transition-all duration-500"
      style={getBackgroundStyle()}
      aria-labelledby="hero-heading"
    >
      {/* Loading overlay with smooth transition */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-rose-400 to-pink-400 rounded-3xl transition-opacity duration-500 ${
          imageLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      />

      {/* Content overlay for better text readability */}
      <div className="absolute inset-0 rounded-3xl" />

      <motion.div
        className="flex-1 max-w-[600px] relative z-10"
        variants={fadeInLeft}
        initial="initial"
        animate="animate"
      >
        <header>
          {/* <p className="mb-5 text-base font-semibold tracking-widest leading-6 text-white uppercase drop-shadow-lg">
            Take Care of the Child
          </p> */}
          <h1
            id="hero-heading"
            className="mb-4 text-5xl font-bold text-white leading-[58px] max-md:text-4xl max-md:leading-10 max-sm:text-3xl max-sm:leading-9 drop-shadow-lg"
          >
            Rent.Play.Repeat
          </h1>
          <p className="mb-8 text-lg leading-7 text-white max-sm:text-base drop-shadow-md">
            Premium toys delivered to your doorstep. Safe,Sanitized and Affordable.
          </p>
          <motion.button
            onClick={handleShopNow}
            className="px-8 py-4 text-base font-bold text-rose-400 uppercase bg-white transition-all cursor-pointer duration-[0.3s] ease-[ease] rounded-[90px] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-rose-400 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Shop now for children's toys"
          >
            Shop Now
          </motion.button>
        </header>
      </motion.div>
    </section>
  );
}

export default HeroSection;
